import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { canAccessProjectPL } from '../../utils/projectPLAccess';
import { canViewAgentActivity } from '../../utils/agentActivityAccess';
import { useContractorPayablesAccess } from '../../hooks/useContractorPayablesAccess';
import { useContractorTimesheetAccess } from '../../hooks/useContractorTimesheetAccess';
import { useBillingPayablesAccess } from '../../hooks/useBillingPayablesAccess';
import { useCardExpenseAccess } from '../../hooks/useCardExpenses';
import { shouldShowCardExpensesNavLink } from '../../utils/cardExpenseAccess';
import { buildSupplierPortalPreviewSearch } from '../../utils/vendorPortalPreviewQuery';
import Logo from './Logo';
import NotificationCenter from './NotificationCenter';
import FeedbackButton from '../feedback/FeedbackButton';
import CrmSearchButton from '../search/CrmSearchButton';
import CrmThemeToggle from './CrmThemeToggle';
import SanitizeFinanceButton from './SanitizeFinanceButton';
import '../pm/pm-shared.css';
import {
  Bell,
  Factory,
  Image,
  Package,
  Database,
  UserCircle,
  Wrench,
  ClipboardList,
  Mic,
  Landmark,
  Map,
  CreditCard,
  Target,
  Clock,
  Activity,
  Receipt,
} from 'lucide-react';
import './AppHeader.css';
import UserAvatar from './UserAvatar';
import PeopleNavDropdown from '../people/PeopleNavDropdown';
import Contractor1099Header from '../pm/Contractor1099Header';
import {
  canAccessAnalyticsNav,
  canAccessSalesActivityNav,
  canAccessSalesCrmNav,
  canPreviewPartnerPortals,
  isFullCrmEmployeeAccess,
  isInternal1099ContractorUser,
  isScopedInternalEmployeeRole,
} from '../../utils/userRoles';

export interface AppHeaderProps {
  /**
   * Staff preview: render the limited supplier nav (Vendor Portal + Projects→Production)
   * for this org, carrying `?viewAs=` (and `as=employee` when previewing employees)
   * on nav links so the preview follows navigation.
   */
  supplierNavPreviewOrgId?: string | null;
  supplierNavPreviewRole?: 'owner' | 'employee' | null;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  supplierNavPreviewOrgId = null,
  supplierNavPreviewRole = null,
}) => {
  const { user, logout } = useAuth();
  const { crmUsers } = useCRM();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const previewingSupplierNav = !!supplierNavPreviewOrgId && user?.role !== 'supplier';
  const isSupplierUser = user?.role === 'supplier' || previewingSupplierNav;
  const supplierViewAsSuffix = previewingSupplierNav
    ? buildSupplierPortalPreviewSearch({
        orgId: supplierNavPreviewOrgId!,
        role: supplierNavPreviewRole === 'employee' ? 'employee' : 'owner',
      })
    : '';

  // Handle clicking a lead from notifications
  const handleLeadClick = useCallback((leadId: string, tab?: 'details' | 'activities' | 'tracking') => {
    // Navigate to home and trigger lead selection
    if (location.pathname !== '/') {
      navigate('/', { state: { selectedLeadId: leadId, tab } });
    } else {
      // Dispatch custom event to select lead on map
      window.dispatchEvent(new CustomEvent('selectLead', { detail: { leadId, tab } }));
    }
  }, [navigate, location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Route to integrations settings when returning from Google OAuth
  useEffect(() => {
    if (user?.role === 'supplier') return;
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state === 'google_calendar' && !location.pathname.startsWith('/settings')) {
      navigate(`/settings/integrations${window.location.search}`, { replace: true });
    }
  }, [user?.role, location.pathname, navigate]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isAnalyticsActive = () => {
    const path = location.pathname;
    const owned = ['/analytics', '/utm-strategy', '/sales-activity', '/finances'];
    return owned.some(p => path === p || path.startsWith(`${p}/`));
  };

  // Project P&L: admins + Project Manager or Executive Assistant designation.
  const userCanAccessProjectPL = useMemo(() => {
    const me = crmUsers.find(u => u.uid === user?.id);
    return canAccessProjectPL({ authRole: user?.role, designations: me?.designations });
  }, [user?.role, user?.id, crmUsers]);

  // The Leads nav now points straight at the list view; the legacy map (`/`)
  // and messages (`/messages`) routes still render that same nav as active so
  // the header reflects the user's actual location.
  const isLeadsActive = () => {
    return (
      location.pathname === '/' ||
      location.pathname === '/leads' ||
      location.pathname === '/list' ||
      location.pathname === '/messages' ||
      location.pathname.startsWith('/client/')
    );
  };

  const isPeopleActive = () =>
    location.pathname === '/people' ||
    location.pathname.startsWith('/people/') ||
    location.pathname.startsWith('/team-chat') ||
    location.pathname.startsWith('/contractor-timesheets');

  const isAdminUser = user?.role === 'admin';
  const { canAccess: canFinancesNav, canFullFinances: canFullFinancesNav } = useContractorPayablesAccess();
  const showFinancesNav = canFinancesNav || canFullFinancesNav || userCanAccessProjectPL;
  const financesNavPath = canFullFinancesNav
    ? '/finances'
    : userCanAccessProjectPL
      ? '/finances/project-pl'
      : '/finances/contractor-payables';
  const { canAccess: canContractorTimesheetsNav } = useContractorTimesheetAccess();
  const { canView: canBillingPayablesNav } = useBillingPayablesAccess();
  const isEmployeeNav = isFullCrmEmployeeAccess({
    authRole: user?.role,
    designations: user?.designations,
  });
  const isInternal1099Nav = isInternal1099ContractorUser({
    role: user?.role,
    is1099Contractor: user?.is1099Contractor,
  });
  const isScopedNav =
    isScopedInternalEmployeeRole(user?.role) && !isEmployeeNav && !isInternal1099Nav;
  const canExternalPortalsNav = canPreviewPartnerPortals(user?.role);
  const showPeopleNav = isEmployeeNav || isScopedNav || canContractorTimesheetsNav;
  const showSalesCrmNav = canAccessSalesCrmNav(user?.role, user?.designations);
  const showSalesActivityNav = canAccessSalesActivityNav(user?.role, user?.designations);
  const showAnalyticsNav = canAccessAnalyticsNav(user?.role, user?.designations);
  const cardExpenseDesignations = useMemo(() => {
    const me = crmUsers.find(u => u.uid === user?.id);
    return me?.designations ?? user?.designations;
  }, [crmUsers, user?.id, user?.designations]);
  const canSeeAgentActivity = useMemo(
    () =>
      canViewAgentActivity({
        authRole: user?.role,
        designations: cardExpenseDesignations,
      }),
    [user?.role, cardExpenseDesignations],
  );
  const { canAccess: canAccessCardExpenses, pendingCount: pendingCardExpenseCount } = useCardExpenseAccess(
    user?.id,
    user?.role,
    cardExpenseDesignations,
  );

  const isProjectsNavActive =
    // /project-pl redirects into Finances; keep it out of Projects.
    (location.pathname.startsWith('/project') && !location.pathname.startsWith('/project-pl')) ||
    location.pathname.startsWith('/materials') ||
    location.pathname.startsWith('/operations') ||
    location.pathname === '/map' ||
    location.pathname.startsWith('/billing-payables') ||
    location.pathname.startsWith('/purchasing') ||
    location.pathname.startsWith('/marketing-hub') ||
    location.pathname === '/designer-portal' ||
    location.pathname === '/architect-portal' ||
    location.pathname === '/engineer-portal' ||
    location.pathname.startsWith('/designer-portal/') ||
    location.pathname.startsWith('/architect-portal/') ||
    location.pathname.startsWith('/engineer-portal/') ||
    location.pathname === '/supplier-rfqs' ||
    location.pathname === '/vendor-admin' ||
    location.pathname === '/vendors' ||
    location.pathname.startsWith('/my-rfqs') ||
    location.pathname.startsWith('/vendor-portal') ||
    location.pathname === '/external-portals';

  if (isInternal1099Nav) {
    return <Contractor1099Header />;
  }

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          <nav className="header-nav">
            <button
              className="header-brand"
              onClick={() => {
                if (isSupplierUser) {
                  navigate(`/vendor-portal${supplierViewAsSuffix}`);
                } else if (isScopedNav) {
                  navigate(showSalesCrmNav ? '/leads' : '/project-hub');
                } else {
                  navigate('/executive');
                }
              }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              aria-label={isSupplierUser ? 'Go to Vendor Portal' : 'Open Executive Suite'}
            >
              <Logo size="small" className="header-logo" />
            </button>
            {isSupplierUser && (
              <>
                <Link
                  to={`/vendor-portal${supplierViewAsSuffix}`}
                  className={`nav-item ${location.pathname.startsWith('/vendor-portal') || location.pathname.startsWith('/my-rfqs') ? 'active' : ''}`}
                >
                  Vendor Portal
                </Link>
                <div className="nav-dropdown-container">
                  <div
                    className={`nav-item nav-dropdown-trigger ${location.pathname.startsWith('/project-hub') || location.pathname.startsWith('/shop-schedule') ? 'active' : ''}`}
                  >
                    <Link to={`/project-hub${supplierViewAsSuffix}`} className="nav-link-text">
                      Projects
                    </Link>
                    <svg
                      className="nav-dropdown-arrow"
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  <div className="nav-dropdown-menu">
                    <Link
                      to={`/project-hub${supplierViewAsSuffix}`}
                      title="All projects (H)"
                      aria-keyshortcuts="H"
                      className={`nav-dropdown-item ${location.pathname.startsWith('/project-hub') || location.pathname.startsWith('/shop-schedule') ? 'active' : ''}`}
                    >
                      <span className="nav-dropdown-icon">
                        <Factory size={18} />
                      </span>
                      Project Hub
                    </Link>
                  </div>
                </div>
              </>
            )}
            {!isSupplierUser && (
            <>
            {showSalesCrmNav && (
            <Link
              to="/leads"
              className={`nav-item ${isLeadsActive() ? 'active' : ''}`}
            >
              Leads
            </Link>
            )}
            {showSalesCrmNav && (
            <div className="nav-dropdown-container">
              <div className={`nav-item nav-dropdown-trigger ${location.pathname.startsWith('/pipeline') || location.pathname.startsWith('/proposals') || location.pathname.startsWith('/won-deals') || location.pathname.startsWith('/design-tracker') ? 'active' : ''}`}>
                <Link to="/pipeline" className="nav-link-text">
                  Pipeline
                </Link>
                <svg
                  className="nav-dropdown-arrow"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <div className="nav-dropdown-menu">
                <Link
                  to="/pipeline"
                  className={`nav-dropdown-item ${location.pathname === '/pipeline' ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="9"></rect>
                      <rect x="14" y="3" width="7" height="5"></rect>
                      <rect x="14" y="12" width="7" height="9"></rect>
                      <rect x="3" y="16" width="7" height="5"></rect>
                    </svg>
                  </span>
                  Pipeline Board
                </Link>
                <Link
                  to="/proposals"
                  className={`nav-dropdown-item ${location.pathname === '/proposals' ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                    </svg>
                  </span>
                  Proposals Summary
                </Link>
                <Link
                  to="/won-deals"
                  className={`nav-dropdown-item ${location.pathname === '/won-deals' ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                      <path d="M4 22h16"></path>
                      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                    </svg>
                  </span>
                  Won Deals
                </Link>

              </div>
            </div>
            )}
            <div className="nav-dropdown-container">
              <button
                className={`nav-item nav-dropdown-trigger ${isProjectsNavActive ? 'active' : ''}`}
              >
                Projects
                <svg
                  className="nav-dropdown-arrow"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div className="nav-dropdown-menu">
                <Link
                  to="/project-hub"
                  title="All projects (H)"
                  aria-keyshortcuts="H"
                  className={`nav-dropdown-item ${location.pathname.startsWith('/project-hub') ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <Package size={18} />
                  </span>
                  Project Hub
                </Link>
                <Link
                  to="/operations"
                  className={`nav-dropdown-item ${location.pathname === '/operations' ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <Wrench size={18} />
                  </span>
                  Operations
                </Link>
                {(!isScopedNav || showSalesCrmNav) && (
                  <>
                <Link
                  to="/map"
                  className={`nav-dropdown-item ${location.pathname === '/map' ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <Map size={18} />
                  </span>
                  CRM Map
                </Link>
                <Link
                  to="/materials"
                  className={`nav-dropdown-item ${location.pathname === '/materials' ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <Database size={18} />
                  </span>
                  Materials Database
                </Link>
                  </>
                )}
                {!isScopedNav && canBillingPayablesNav && (
                  <Link
                    to="/billing-payables"
                    className={`nav-dropdown-item ${location.pathname.startsWith('/billing-payables') ? 'active' : ''}`}
                  >
                    <span className="nav-dropdown-icon">
                      <Receipt size={18} />
                    </span>
                    Billing &amp; Payables
                  </Link>
                )}
                <Link
                  to="/purchasing"
                  className={`nav-dropdown-item ${location.pathname.startsWith('/purchasing') ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <ClipboardList size={18} />
                  </span>
                  Purchasing
                </Link>
                {!isScopedNav && (
                <Link
                  to="/marketing-hub"
                  className={`nav-dropdown-item ${location.pathname.startsWith('/marketing-hub') ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <Mic size={18} />
                  </span>
                  Marketing Hub
                </Link>
                )}
                {(canExternalPortalsNav || isEmployeeNav) && (
                  <>
                    <div className="nav-dropdown-divider" />
                    {canExternalPortalsNav && (
                      <Link
                        to="/external-portals"
                        className={`nav-dropdown-item ${location.pathname === '/external-portals' ? 'active' : ''}`}
                      >
                        <span className="nav-dropdown-icon">
                          <UserCircle size={18} />
                        </span>
                        External Portals
                      </Link>
                    )}
                    {isEmployeeNav && (
                      <Link
                        to="/vendor-admin"
                        className={`nav-dropdown-item ${location.pathname.startsWith('/supplier-rfqs') || location.pathname.startsWith('/vendor-admin') || location.pathname.startsWith('/vendors') ? 'active' : ''}`}
                      >
                        <span className="nav-dropdown-icon">
                          <ClipboardList size={18} />
                        </span>
                        Vendors
                      </Link>
                    )}
                  </>
                )}
              </div>
            </div>
            {showSalesActivityNav && (
            <div className="nav-dropdown-container">
              <button
                className={`nav-item nav-dropdown-trigger ${isAnalyticsActive() ? 'active' : ''}`}
              >
                Analytics
                <svg
                  className="nav-dropdown-arrow"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div className="nav-dropdown-menu">
                <Link
                  to="/sales-activity"
                  className={`nav-dropdown-item ${isActive('/sales-activity') || location.pathname === '/analytics' ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                  </span>
                  Sales Activity
                </Link>
                {isAdminUser && (
                  <Link
                    to="/analytics/api-usage"
                    className={`nav-dropdown-item ${isActive('/analytics/api-usage') ? 'active' : ''}`}
                  >
                    <span className="nav-dropdown-icon">
                      <Activity size={18} />
                    </span>
                    API usage
                  </Link>
                )}
                {showFinancesNav && (
                  <Link
                    to={financesNavPath}
                    className={`nav-dropdown-item ${isActive('/finances') ? 'active' : ''}`}
                  >
                    <span className="nav-dropdown-icon">
                      <Landmark size={18} />
                    </span>
                    {canFullFinancesNav || userCanAccessProjectPL ? 'Finances' : 'Contractor Payables'}
                  </Link>
                )}
                {showAnalyticsNav && (
                  <>
                <div className="nav-dropdown-divider" />
                <Link
                  to="/utm-strategy"
                  className={`nav-dropdown-item ${isActive('/utm-strategy') ? 'active' : ''}`}
                >
                  <span className="nav-dropdown-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  </span>
                  UTM Builder
                </Link>
                  </>
                )}
              </div>
            </div>
            )}
            {showPeopleNav && (
              <div className="nav-dropdown-container">
                <button
                  type="button"
                  data-emp-award-filing-target
                  className={`nav-item nav-dropdown-trigger ${isPeopleActive() ? 'active' : ''}`}
                >
                  People
                  <svg
                    className="nav-dropdown-arrow"
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                <div className="nav-dropdown-menu">
                  <PeopleNavDropdown variant={isEmployeeNav ? 'employee' : 'fieldStaff'} />
                </div>
              </div>
            )}
            </>
            )}
          </nav>
        </div>

        <div className="header-right">
          {!isSupplierUser && <SanitizeFinanceButton />}
          {!isSupplierUser && !isScopedNav && canSeeAgentActivity && (
            <Link
              to="/agent-activity"
              className={`agent-activity-bell ${location.pathname === '/agent-activity' ? 'active' : ''}`}
              title="Agent Activity"
              aria-label="Agent Activity"
            >
              <Activity size={20} />
            </Link>
          )}
          {!isSupplierUser && <CrmSearchButton />}
          {!isSupplierUser && <FeedbackButton />}
          <CrmThemeToggle />
          {!isSupplierUser && <NotificationCenter onLeadClick={handleLeadClick} />}
          <div className="user-menu-container" ref={menuRef}>
            <button
              className={`user-menu-trigger ${showUserMenu ? 'open' : ''}`}
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-expanded={showUserMenu}
              aria-haspopup="true"
            >
              <span className="user-avatar-wrap">
                <UserAvatar
                  className="user-avatar"
                  name={user?.name || 'User'}
                  photoURL={user?.photoURL}
                  userId={user?.id}
                />
              </span>
              <span className="user-name">{user?.name}</span>
              <svg
                className={`dropdown-arrow ${showUserMenu ? 'open' : ''}`}
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {showUserMenu && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <span className="dropdown-name">{user?.name}</span>
                  <span className="dropdown-email">{user?.email}</span>
                </div>
                <div className="dropdown-divider" />
                {!isSupplierUser && (
                  <>
                    <Link
                      to="/settings/account"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Bell size={16} />
                      Notification Settings
                    </Link>
                    {shouldShowCardExpensesNavLink(canAccessCardExpenses) && (
                      <Link
                        to="/card-expenses"
                        className="dropdown-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <CreditCard size={16} />
                        My Card Expenses
                        {pendingCardExpenseCount > 0 && (
                          <span className="dropdown-badge">{pendingCardExpenseCount}</span>
                        )}
                      </Link>
                    )}
                    {canContractorTimesheetsNav && (
                      <Link
                        to="/contractor-timesheets"
                        className="dropdown-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Clock size={16} />
                        Contractors
                      </Link>
                    )}
                    {showSalesCrmNav && isScopedNav && (
                    <Link
                      to="/settings"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                      Settings
                    </Link>
                    )}
                    {!isScopedNav && (
                      <>
                    <Link
                      to="/media-management"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Image size={16} />
                      Media library
                    </Link>
                    <Link
                      to="/tools"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Wrench size={16} />
                      Tools
                    </Link>
                    {isAdminUser && (
                      <Link
                        to="/tools/lifetime-goals"
                        className="dropdown-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Target size={16} />
                        Lifetime Goals
                      </Link>
                    )}
                    <Link
                      to="/settings"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                      Settings
                    </Link>
                      </>
                    )}
                  </>
                )}
                {isSupplierUser && (
                  <Link
                    to="/vendor-portal"
                    className="dropdown-item"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <ClipboardList size={16} />
                    Vendor Portal
                  </Link>
                )}
                <div className="dropdown-divider" />
                <button
                  className="dropdown-item logout"
                  onClick={handleLogout}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

    </>
  );
};

export default AppHeader;
